<template>
    <div id="dojo-reports-view"
         class="full-vue-page">
        <div class="container container--top-padding-for-mobile-header">
            <div id="dojo-reports-title"
                 class="no-print hidden-xs invert">
                <h2>{{ l('DOJO_REPORTS.TITLE') }}</h2>
                <div class="btn-grid">
                    <button class="btn btn-lg btn-info"
                            :disabled="!reportsEnabled"
                            @click.prevent="showBankingReport">
                        {{ l('DOJO_REPORTS.BANKING_REPORT_BUTTON') }}
                    </button>

                    <button class="btn btn-lg btn-info"
                            :disabled="!reportsEnabled"
                            @click.prevent="showEndOfDayReport">
                        {{ l('DOJO_REPORTS.END_OF_DAY_REPORT_BUTTON') }}
                    </button>

                    <button class="btn btn-lg btn-info"
                            :disabled="!reportsEnabled"
                            @click.prevent="showXBalanceReport">
                        {{ l('DOJO_REPORTS.X_BALANCE_REPORT_BUTTON') }}
                    </button>

                    <button class="btn btn-lg btn-info"
                            :disabled="!reportsEnabled"
                            @click.prevent="showZBalanceReport">
                        {{ l('DOJO_REPORTS.Z_BALANCE_REPORT_BUTTON') }}
                    </button>
                </div>

                <div v-if="reportsEnabled === false"
                     class="alert alert-info mt-1">
                    <p>{{ l('DOJO_REPORTS.REPORTS_NO_SUPPORTED') }}</p>
                </div>

                <div v-if="errorMessage"
                     class="alert alert-danger mt-1">
                    <p>{{ errorMessage }}</p>
                </div>
                <div v-else>
                    <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>
                    <DojoReport v-if="!loading"
                                :lines="reportLines"
                                @print-report="printReport"></DojoReport>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DojoReport from './components/DojoReport';
import DojoReportResultStatus from '@/lib/PaymentMethods/Dojo/DojoReportResultStatus';
import DojoReportType from '@/lib/PaymentMethods/Dojo/DojoReportType';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PrintJobOptions from '@/lib/Printing/PrintJobOptions';
import PrintType from '@/lib/Printing/PrintType';
import { container } from '@/container';
import { getService } from '@/lib/DI/injector';

export default {
    name: 'DojoReportsView',

    components: {
        PageLoadingSpinner,
        DojoReport,
    },

    data() {
        return {
            dojoReport: null,
            loading: false,
            reportLines: [],
            errorMessage: null,
            isFinished: false,
            reportsEnabled: null,
        };
    },

    watch: {
        dojoReport: {
            handler(report) {
                if (report.isFinished) {
                    this.loading = false;
                    this.reportLines = report.latestResponse.data.reportLines;

                    if (report.reportResult.message !== DojoReportResultStatus.COMPLETED) {
                        this.errorMessage = report.reportResult.message;
                    }
                }
            },
            deep: true,
        },
    },

    beforeCreate() {
        try {
            getService('dojoDriver');
        } catch (error) {
            console.error('Dojo payment method is not available', error);
            window.location.href = '/epos/';
        }
    },

    created() {
        try {
            container.dojoDriver.init();
            // The menu button that links to this page is disabled if supportsReports === false but the page may have
            // been bookmarked so we should disable the buttons and show a useful message.
            this.reportsEnabled = !!container.dojoDriver.config.supportsReports;
        } catch (error) {
            this.errorMessage = error.message;
        }
    },

    methods: {
        showBankingReport() {
            this.getReport(DojoReportType.BANKING);
        },

        showEndOfDayReport() {
            this.getReport(DojoReportType.END_OF_DAY);
        },

        showXBalanceReport() {
            this.getReport(DojoReportType.X_BALANCE);
        },

        showZBalanceReport() {
            this.getReport(DojoReportType.Z_BALANCE);
        },

        async getReport(reportType) {
            try {
                this.dojoReport = container.dojoDriver.createReportRequest(reportType);

                this.loading = true;
                this.reportLines = [];

                let result = await container.dojoDriver.sendReportRequest(this.dojoReport);
                if (result) {
                    await container.dojoDriver.pollReport(this.dojoReport);
                } else {
                    this.loading = false;
                }
            } catch (e) {
                console.error(e);
                this.errorMessage = e.message;
            } finally {
                this.loading = false;
            }
        },

        async printReport() {
            try {
                let opts = new PrintJobOptions(
                    PrintType.REPORT
                );

                await container.printer.printHtmlString(
                    document.getElementById('dojo-report').outerHTML,
                    opts
                );
            } catch (error) {
                container.toastFactory.error('REPORTS.PRINT_ERROR');
            }
        },
    },
};
</script>

<style lang="less" scoped>
@import "../../../../less/variables";

@media (min-width: @non-mobile-min) {
    .btn-grid {
        // Cheeky 4 col grid because there will always be 4 buttons here.
        grid-template-columns: repeat(4, 1fr);
    }
}

</style>
